import { Alert, Button, Skeleton, Switch } from '@mantine/core';
import { IconAlertCircle, IconRefresh, IconSignature } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { Panel } from '../../Panel.js';
import { CardFooter, settingsClasses } from './SettingsLayout.js';

/** Şirketin onay ayarı (ADR 0056 §4). */
export interface ApprovalSettings {
  reauthAlways: boolean;
  /** Ayar satırının sürümü; satır yoksa `null`. */
  rowVersion: number | null;
}

export interface ApprovalSettingsSource {
  load(): Promise<ApprovalSettings>;
  /** Yoksa salt okunur ("Ayarlar / düzenleme" yetkisi yok). */
  save?: (rowVersion: number | null, reauthAlways: boolean) => Promise<ApprovalSettings>;
}

export interface ApprovalSettingsPanelProps {
  /** `null`: okunuyor ya da okunamadı. */
  settings: ApprovalSettings | null;
  error?: boolean;
  onRetry?: () => void;
  onSave?: (rowVersion: number | null, reauthAlways: boolean) => Promise<void>;
}

/**
 * Ayarlar ▸ Onaylar (ADR 0056 §4): "Onayda parolayı her yerde iste". Kapalıyken PMS, dinlenme
 * saatleri, satın alma ve sefer raporu parola ister; diğer onaylarda açık onay adımı yeterlidir.
 * Web ofiste düzenlenir; gemiye senkronla iner.
 */
export function ApprovalSettingsPanel({
  settings,
  error,
  onRetry,
  onSave,
}: ApprovalSettingsPanelProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`approvalPolicy.settings.${key}`);
  const [value, setValue] = useState(settings?.reauthAlways ?? false);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings) setValue(settings.reauthAlways);
  }, [settings]);
  const body = (() => {
    if (error) {
      return (
        <EmptyState
          tone="danger"
          title={t('common.loadError')}
          action={
            onRetry ? (
              <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={onRetry}>
                {k('retry')}
              </Button>
            ) : undefined
          }
        />
      );
    }
    if (!settings) {
      return (
        <div aria-busy="true">
          <Skeleton height={18} mb={10} />
          <Skeleton height={18} />
        </div>
      );
    }
    return (
      <>
        <p className={settingsClasses.intro}>{k('text')}</p>
        <Switch
          label={k('reauthAlways')}
          description={k('reauthAlwaysHint')}
          checked={value}
          disabled={!onSave || busy}
          onChange={(event) => {
            setSaved(false);
            setFailure(false);
            setValue(event.currentTarget.checked);
          }}
        />
        {failure ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />} role="alert" mt="sm">
            {k('error')}
          </Alert>
        ) : saved ? (
          <Alert color="green" role="status" mt="sm">
            {k('saved')}
          </Alert>
        ) : null}
        {onSave ? (
          <CardFooter>
            <Button
              loading={busy}
              disabled={value === settings.reauthAlways}
              onClick={async () => {
                setBusy(true);
                setFailure(false);
                try {
                  await onSave(settings.rowVersion, value);
                  setSaved(true);
                } catch {
                  setFailure(true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {k('save')}
            </Button>
          </CardFooter>
        ) : null}
      </>
    );
  })();
  return (
    <Panel title={k('title')} icon={<IconSignature size={18} stroke={1.8} />} divider>
      {body}
    </Panel>
  );
}

/** Kaynağa bağlı bölüm: okur, kaydeder, kaydedileni gösterir. */
export function ApprovalSettingsSection({ source }: { source: ApprovalSettingsSource }) {
  const [settings, setSettings] = useState<ApprovalSettings | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` yeniden okutur
  useEffect(() => {
    let live = true;
    setError(false);
    source
      .load()
      .then((value) => {
        if (live) setSettings(value);
      })
      .catch(() => {
        if (live) setError(true);
      });
    return () => {
      live = false;
    };
  }, [source, attempt]);
  const save = source.save;
  return (
    <ApprovalSettingsPanel
      settings={settings}
      error={error}
      onRetry={() => setAttempt((n) => n + 1)}
      {...(save
        ? {
            onSave: async (rowVersion: number | null, reauthAlways: boolean) => {
              setSettings(await save(rowVersion, reauthAlways));
            },
          }
        : {})}
    />
  );
}
